/**
 * Shared query hooks used by the shell (jobs, search, views, notes, health,
 * identity). Slice-specific hooks live in src/views/<slice>/.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import {
  deleteJson,
  fetchJson,
  postJson,
  putJson,
  type ApiResult,
  type FetchOptions,
  type QueryParams,
} from "./client";
import { paths } from "./client";
import { qk, keysForSseJob } from "./keys";
import { tokenJobId } from "./types";
import type {
  Deleted,
  Health,
  Identity,
  IdentityWrite,
  JobHealth,
  JobsResponse,
  JobSummary,
  TokenJob,
  Note,
  NotesQuery,
  NoteWrite,
  OwnedSetting,
  SearchQuery,
  SearchResponse,
  View,
  ViewsQuery,
  ViewWrite,
} from "./types";

type Opts<T> = Omit<UseQueryOptions<ApiResult<T>, Error>, "queryKey" | "queryFn"> & FetchOptions;

/**
 * Generic typed GET query. `key` from `qk`, `path` from `paths`. `opts.silent`
 * keeps the response's meta.warnings / bucket out of the global UI.
 */
export function useApi<T>(key: readonly unknown[], path: string, params?: QueryParams, opts?: Opts<T>) {
  const { silent, ...query } = opts ?? {};
  return useQuery<ApiResult<T>, Error>({
    queryKey: key,
    queryFn: ({ signal }) => fetchJson<T>(path, params, signal, { silent }),
    ...query,
  });
}

export function useJobs() {
  return useApi<JobsResponse>(qk.jobs(), paths.jobs(), undefined, {
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useJobSummary(chain: string | undefined, token: string | undefined) {
  return useApi<JobSummary>(
    qk.summary(chain ?? "", token ?? ""),
    `${paths.job(chain ?? "", token ?? "")}/summary`,
    undefined,
    {
      enabled: !!chain && !!token,
    },
  );
}

export function useSaveTradeSizes(chain: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { small_usd: string; big_usd: string; whale_usd: string }) =>
      putJson<TokenJob>(`${paths.job(chain, token)}/trade-sizes`, body),
    onSuccess: async () => {
      await Promise.all(
        keysForSseJob(tokenJobId(chain, token)).map((queryKey) => qc.invalidateQueries({ queryKey })),
      );
    },
  });
}

/** Pause or resume token-flow's following of one token; the jobs list refetches on success. */
export function useSetPaused(chain: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paused: boolean) => putJson<TokenJob>(`${paths.job(chain, token)}/tracking`, { paused }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.jobs() });
    },
  });
}

export function useSearch(q: SearchQuery) {
  return useApi<SearchResponse>(
    qk.search(q),
    paths.search(),
    { ...q },
    { enabled: q.q.trim().length >= 2, staleTime: 5_000 },
  );
}

export function useViews(q: ViewsQuery = {}) {
  return useApi<View[]>(qk.views(q), paths.views(), { ...q });
}

export function useSaveView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ViewWrite) => postJson<View>(paths.views(), v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["frontend", "views"] }),
  });
}

export function useDeleteView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJson<Deleted>(`${paths.views()}/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["frontend", "views"] }),
  });
}

export function useNotes(q: NotesQuery = {}, enabled = true) {
  return useApi<Note[]>(qk.notes(q), paths.notes(), { ...q }, { enabled });
}

export function useSaveNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (n: NoteWrite) => postJson<Note>(paths.notes(), n),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["frontend", "notes"] }),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJson<Deleted>(`${paths.notes()}/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["frontend", "notes"] }),
  });
}

/**
 * One address's identity. `token` asks for it as the address looks inside
 * that market; without it, the chain-wide view.
 */
export function useIdentity(chain: string | undefined, address: string | undefined, token?: string) {
  return useApi<Identity>(
    qk.identity(chain ?? "", address ?? "", token),
    paths.identity(chain ?? "", address ?? "", token),
    undefined,
    {
      enabled: !!chain && !!address,
    },
  );
}

/**
 * What an identity write moves: the address book, every job's rows and the
 * positions page (a name, or an owned mark, which the form can carry too).
 * Called on success rather than left to SSE, which may be off.
 */
export function invalidateIdentity(qc: Pick<ReturnType<typeof useQueryClient>, "invalidateQueries">) {
  return Promise.all(
    [["addressbook"], ["job"], qk.myPositions()].map((queryKey) => qc.invalidateQueries({ queryKey })),
  );
}

/**
 * Writes the row at the same scope `useIdentity` reads with the same
 * `token`. Passing one here and not there (or the reverse) edits a
 * different row than the one on screen.
 */
export function useWriteIdentity(chain: string, address: string, token?: string, scope?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (w: IdentityWrite) => putJson<Identity>(paths.identity(chain, address, token, scope), w),
    onSuccess: () => invalidateIdentity(qc),
  });
}

/**
 * Mark an address as the operator's own, or take the mark away: one field,
 * one request, like the star. Without `scope` the server writes the
 * every-chain row, whatever token is in view.
 */
export function useOwnAddress(chain: string, address: string, token?: string, scope?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (owned: OwnedSetting) =>
      putJson<Identity>(paths.identityOwned(chain, address, token, scope), { owned }),
    onSuccess: () => invalidateIdentity(qc),
  });
}

/**
 * Bookmark an address at the scope `useIdentity` reads with the same
 * `token`: one flag, one request, no form. The stored name, tags and role
 * are not sent and so cannot be lost to a click.
 */
export function useStarAddress(chain: string, address: string, token?: string, scope?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (starred: boolean) =>
      putJson<Identity>(paths.identityStar(chain, address, token, scope), { starred }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["addressbook"] });
      void qc.invalidateQueries({ queryKey: ["job"] });
    },
  });
}

export function useHealth(refetchMs = 15_000) {
  return useApi<Health>(qk.health(), paths.health(), undefined, { refetchInterval: refetchMs });
}

export function useJobHealth(jobId: string | undefined) {
  return useApi<JobHealth>(qk.jobHealth(jobId ?? ""), `${paths.health()}/${jobId ?? ""}`, undefined, {
    enabled: !!jobId,
  });
}
